import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Bot, MessageSquare, Sparkles } from 'lucide-react';
import api from '../../services/api';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';

// ─── Custom node renderers ───────────────────────────────────────────────────

const NODE_COLORS = {
  start: '#10b981',
  end: '#ef4444',
  message: '#3b82f6',
  question: '#f59e0b',
  condition: '#8b5cf6',
  llm: '#ec4899',
  api_call: '#6366f1',
  human_handover: '#64748b',
};

function FlowNode({ data, type }) {
  const color = NODE_COLORS[type] || '#6b7280';
  return (
    <div className="bg-white border-2 rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] min-w-[160px] max-w-[220px]"
      style={{ borderColor: color }}>
      <Handle type="target" position={Position.Top} style={{ background: color }} />
      <div className="px-3 py-1.5 text-xs font-bold rounded-t-lg text-white"
        style={{ backgroundColor: color }}>
        {type.replace('_', ' ')}
      </div>
      <div className="px-3 py-2 text-xs text-[#444444] leading-relaxed">
        {data.text || data.message || data.label || '(no text)'}
      </div>
      {type === 'condition' && (
        <div className="px-3 pb-2 text-xs text-[#706E6B] italic">{data.condition || 'condition…'}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: color }} />
      {type === 'condition' && (
        <>
          <Handle type="source" id="yes" position={Position.Right} style={{ background: '#10b981', top: '60%' }} />
          <Handle type="source" id="no" position={Position.Left} style={{ background: '#ef4444', top: '60%' }} />
        </>
      )}
    </div>
  );
}

const nodeTypes = {
  start: (p) => <FlowNode {...p} type="start" />,
  end: (p) => <FlowNode {...p} type="end" />,
  message: (p) => <FlowNode {...p} type="message" />,
  question: (p) => <FlowNode {...p} type="question" />,
  condition: (p) => <FlowNode {...p} type="condition" />,
  llm: (p) => <FlowNode {...p} type="llm" />,
  api_call: (p) => <FlowNode {...p} type="api_call" />,
  human_handover: (p) => <FlowNode {...p} type="human_handover" />,
};

// ─── Node editor panel ───────────────────────────────────────────────────────

function NodeEditor({ node, onChange, onClose }) {
  const [data, setData] = useState({ ...node.data });

  const save = () => { onChange({ ...node, data }); onClose(); };

  return (
    <div className="absolute right-4 top-4 w-72 bg-white rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] border border-[#E5E5E5] z-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#2E2E2E] capitalize">{node.type.replace('_', ' ')} Step</h3>
        <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444]">✕</button>
      </div>
      <div className="space-y-2">
        {['message', 'question', 'start', 'end', 'human_handover'].includes(node.type) && (
          <div>
            <label className="block text-xs font-medium text-[#706E6B] mb-1">Text</label>
            <textarea
              value={data.text || ''}
              onChange={e => setData(d => ({ ...d, text: e.target.value }))}
              rows={3}
              className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
            />
          </div>
        )}
        {node.type === 'question' && (
          <div>
            <label className="block text-xs font-medium text-[#706E6B] mb-1">Save answer to variable</label>
            <input
              value={data.variable || ''}
              onChange={e => setData(d => ({ ...d, variable: e.target.value }))}
              className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              placeholder="e.g. userName"
            />
          </div>
        )}
        {node.type === 'condition' && (
          <div>
            <label className="block text-xs font-medium text-[#706E6B] mb-1">Regex condition (matched = Yes)</label>
            <input
              value={data.condition || ''}
              onChange={e => setData(d => ({ ...d, condition: e.target.value }))}
              className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              placeholder="e.g. yes|sure|ok"
            />
          </div>
        )}
        {node.type === 'llm' && (
          <>
            <div>
              <label className="block text-xs font-medium text-[#706E6B] mb-1">System Prompt</label>
              <textarea
                value={data.systemPrompt || ''}
                onChange={e => setData(d => ({ ...d, systemPrompt: e.target.value }))}
                rows={4}
                className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#706E6B] mb-1">Knowledge Base ID (optional)</label>
              <input
                value={data.knowledgeBaseId || ''}
                onChange={e => setData(d => ({ ...d, knowledgeBaseId: e.target.value }))}
                className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              />
            </div>
          </>
        )}
        {node.type === 'api_call' && (
          <>
            <div>
              <label className="block text-xs font-medium text-[#706E6B] mb-1">URL</label>
              <input
                value={data.url || ''}
                onChange={e => setData(d => ({ ...d, url: e.target.value }))}
                className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#706E6B] mb-1">Method</label>
              <select
                value={data.method || 'GET'}
                onChange={e => setData(d => ({ ...d, method: e.target.value }))}
                className="w-full text-sm border border-[#D8DDE6] rounded px-3 py-2"
              >
                {['GET', 'POST', 'PUT', 'DELETE'].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </>
        )}
      </div>
      <button
        onClick={save}
        className="mt-4 w-full bg-[#0176D3] hover:bg-[#014486] text-white rounded py-2 text-[13px] font-semibold"
      >
        Save Node
      </button>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

const NODE_TYPES_LIST = ['message', 'question', 'condition', 'llm', 'api_call', 'human_handover', 'end'];

let idCounter = 100;
const newId = () => `node_${++idCounter}`;

export default function FlowBuilderPage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('agentic_mode');
  const [flows, setFlows] = useState([]);
  const [activeFlowId, setActiveFlowId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flowName, setFlowName] = useState('');

  useEffect(() => {
    if (!hasAccess) return;
    api.get('/bot/flows').then(r => setFlows(r.data.data || []));
  }, [hasAccess]);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="AI Chatbot Flows"
        subtitle="Visual chatbot builder with AI-answered conversations"
        icon={Bot}
        plan={plan}
        featureName="Agentic Mode"
        description="Upgrade to unlock the visual chatbot flow builder and AI-answered conversations grounded in your own Knowledge Base."
        highlights={[
          { icon: MessageSquare, label: 'Visual flow builder', desc: 'Messages, questions, branches, human handover' },
          { icon: Sparkles, label: 'AI-answered replies', desc: 'Grounded in your own uploaded documents' },
          { icon: Bot, label: 'Autonomous handling', desc: 'The bot acts on customer messages on its own' },
        ]}
      />
    );
  }

  const loadFlow = async (flowId) => {
    const { data } = await api.get(`/bot/flows/${flowId}`);
    const flow = data.data;
    setActiveFlowId(flow._id);
    setFlowName(flow.name);
    setNodes(flow.nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })));
    setEdges(flow.edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label })));
  };

  const newFlow = () => {
    setActiveFlowId(null);
    setFlowName('New Flow');
    setNodes([{ id: 'start_1', type: 'start', position: { x: 250, y: 50 }, data: { text: 'Start' } }]);
    setEdges([]);
  };

  const addNode = (type) => {
    const id = newId();
    setNodes(ns => [...ns, {
      id,
      type,
      position: { x: 100 + Math.random() * 300, y: 100 + Math.random() * 200 },
      data: { text: `${type} node` },
    }]);
  };

  const saveFlow = async () => {
    setSaving(true);
    try {
      const payload = {
        name: flowName,
        nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label })),
      };
      if (activeFlowId) {
        await api.put(`/bot/flows/${activeFlowId}`, payload);
      } else {
        const { data } = await api.post('/bot/flows', payload);
        setActiveFlowId(data.data._id);
        setFlows(fs => [...fs, data.data]);
      }
    } finally {
      setSaving(false);
    }
  };

  const onNodesChange = useCallback(changes => setNodes(ns => applyNodeChanges(changes, ns)), []);
  const onEdgesChange = useCallback(changes => setEdges(es => applyEdgeChanges(changes, es)), []);
  const onConnect = useCallback(conn => setEdges(es => addEdge({ ...conn, id: `e_${Date.now()}` }, es)), []);
  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);

  return (
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden bg-[#F7F9FB]">
      {/* Left sidebar */}
      <div className="w-full lg:w-56 max-h-[40vh] lg:max-h-none flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E5E5E5] flex flex-col">
        <div className="p-4 border-b border-[#E5E5E5]">
          <h2 className="text-sm font-bold text-[#2E2E2E] mb-2">Bot Flows</h2>
          <button onClick={newFlow} className="w-full text-xs bg-[#0176D3] text-white rounded py-1.5 hover:bg-[#014486]">
            + New Flow
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {flows.map(f => (
            <div
              key={f._id}
              onClick={() => loadFlow(f._id)}
              className={`p-2 rounded-lg cursor-pointer mb-1 text-sm ${f._id === activeFlowId ? 'bg-[#EAF4FE] text-[#0176D3] font-medium' : 'hover:bg-[#F3F2F2] text-[#444444]'}`}
            >
              <div className="flex items-center justify-between">
                <span className="truncate">{f.name}</span>
                {f.isActive && <span className="text-xs text-[#04844B] ml-1">●</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-[#E5E5E5]">
          <p className="text-xs font-medium text-[#706E6B] mb-2">Add Step</p>
          <div className="space-y-1">
            {NODE_TYPES_LIST.map(t => (
              <button
                key={t}
                onClick={() => addNode(t)}
                className="w-full text-left text-xs px-2 py-1.5 rounded-lg hover:bg-[#F3F2F2] text-[#5C5C5C] capitalize"
                style={{ borderLeft: `3px solid ${NODE_COLORS[t]}` }}
              >
                {t.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 flex flex-col relative">
        <div className="bg-white border-b border-[#E5E5E5] px-4 py-2 flex items-center gap-3">
          <input
            value={flowName}
            onChange={e => setFlowName(e.target.value)}
            className="text-sm font-medium border border-[#D8DDE6] rounded px-3 py-1.5 focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
          />
          <button
            onClick={saveFlow}
            disabled={saving}
            className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-4 py-1.5 rounded text-[13px] font-semibold whitespace-nowrap"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {activeFlowId && (
            <button
              onClick={async () => { await api.patch(`/bot/flows/${activeFlowId}/toggle-active`); loadFlow(activeFlowId); }}
              className="text-sm border border-[#E5E5E5] px-3 py-1.5 rounded hover:bg-[#F7F9FB] text-[#5C5C5C] whitespace-nowrap"
            >
              Toggle Active
            </button>
          )}
        </div>

        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        {selectedNode && (
          <NodeEditor
            node={selectedNode}
            onChange={updated => {
              setNodes(ns => ns.map(n => n.id === updated.id ? updated : n));
              setSelectedNode(null);
            }}
            onClose={() => setSelectedNode(null)}
          />
        )}
      </div>
    </div>
  );
}
